// Gestão de Orçamentos — orçamentos mensais por paciente exportados pelo IW
// (Export/ORCAMENTOS/TDEXP_ORCAMENTOS.TXT, a cada 30 min), lidos por api/iw-orcamentos.js.
// Cartões e gráficos do mês escolhido + listas de ação (renovar, sem valor, pendentes de autorização).
// Orçamentos reprovados (autorização interna 3) não entram nas somas.
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList } from 'recharts';
import * as XLSX from 'xlsx';
import { buscarIW, fmtExportacao } from './iw/apiIW';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#6366f1', grid: '#eef2f6' };
const COR_UNIDADE = { Vitalcare: '#2a78d6', HomeBaby: '#eb6834', Outra: '#1baf7a' };
const UNIDADES = ['Vitalcare', 'HomeBaby', 'Outra'];
const COR_NEUTRA = '#64748b';
// Códigos do IW, os mesmos nas duas autorizações (conferidos na tela do IW, Prontuário > Orçamento, em 21/10/2026).
const STATUS_AUTORIZACAO = { 1: ['Aguardando aprovação', '#fef3c7', '#92400e'], 2: ['Aprovado', '#dcfce7', '#166534'], 3: ['Reprovado', '#fee2e2', '#991b1b'] };
const INTERNA = STATUS_AUTORIZACAO;
const OPERADORA = STATUS_AUTORIZACAO;
const LISTAS = [
  { k: 'renovar', l: '🔁 Renovar', desc: 'em atendimento até o fim do mês e sem orçamento no mês seguinte' },
  { k: 'pendentes', l: '⏳ Aguardando aprovação', desc: 'aprovação interna ainda não feita com o período já iniciado' },
  { k: 'semValor', l: '❔ Sem valor', desc: 'orçamento sem valor lançado no IW' },
  { k: 'parciais', l: '✂️ Mês parcial', desc: 'não cobre o mês inteiro (entrada, alta ou troca de pacote no meio)' },
  { k: 'todos', l: 'Todos', desc: '' },
];

const fmtValor = v => (typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—');
const fmtCurto = v => (v >= 1e6 ? `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : v >= 1e3 ? `R$ ${Math.round(v / 1e3).toLocaleString('pt-BR')} mil` : fmtValor(v));
const fmtData = iso => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const NOMES_MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmtMes = m => `${NOMES_MES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}`;
const mesSeguinte = m => { const [a, b] = m.split('-').map(Number); return b === 12 ? `${a + 1}-01` : `${a}-${String(b + 1).padStart(2, '0')}`; };
const fimDoMes = m => { const [a, b] = m.split('-').map(Number); return `${m}-${String(new Date(a, b, 0).getDate()).padStart(2, '0')}`; };
const hojeISO = () => new Date().toLocaleDateString('sv-SE');
// "Assistencia Domiciliar", "ASSISTENCIA DOMICILIAR" e "Assistência Domiciliar" são o mesmo pacote
const chaveTexto = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
const ativo = o => o.autorizacaoInterna !== '3';
const soma = arr => arr.reduce((s, o) => s + (o.valor || 0), 0);

function TooltipValor({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 10, padding: '8px 12px', fontSize: '0.78rem', boxShadow: '0 4px 14px rgba(15,23,42,.08)' }}>
      <div style={{ fontWeight: 800, color: P.text, marginBottom: 4 }}>{payload[0].payload.rotulo || label}</div>
      {payload.map(x => (
        <div key={x.dataKey} style={{ display: 'flex', gap: 8, alignItems: 'center', color: P.text }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: x.color || x.payload.cor }} />
          {payload.length > 1 ? `${x.name}: ` : ''}{fmtValor(x.value)}
        </div>
      ))}
      {payload.length > 1 && <div style={{ fontWeight: 700, marginTop: 4, color: P.text }}>Total: {fmtValor(total)}</div>}
      {payload[0].payload.detalhe && <div style={{ color: P.muted, marginTop: 4 }}>{payload[0].payload.detalhe}</div>}
    </div>
  );
}

function Painel({ titulo, sub, children, style }) {
  return (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, padding: '14px 16px', minWidth: 0, ...style }}>
      <div style={{ fontWeight: 800, color: P.text, fontSize: '0.92rem' }}>{titulo}</div>
      {sub && <div style={{ fontSize: '0.74rem', color: P.muted, marginBottom: 8 }}>{sub}</div>}
      {children}
    </div>
  );
}

// Barra horizontal de proporção (autorizações), com rótulo e número em cada parte.
function BarraStatus({ titulo, partes }) {
  const total = partes.reduce((s, p) => s + p.n, 0) || 1;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: P.text, marginBottom: 6 }}>{titulo}</div>
      <div style={{ display: 'flex', gap: 2, height: 14, borderRadius: 4, overflow: 'hidden', background: P.grid }}>
        {partes.filter(p => p.n).map(p => <div key={p.l} title={`${p.l}: ${p.n}`} style={{ width: `${(p.n / total) * 100}%`, background: p.cor }} />)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6 }}>
        {partes.map(p => (
          <span key={p.l} style={{ fontSize: '0.76rem', color: P.text, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: p.cor }} />{p.l}: <strong>{p.n}</strong> <span style={{ color: P.muted }}>({Math.round((p.n / total) * 100)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function GestaoOrcamentos() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [periodo, setPeriodo] = useState('mes'); // 'mes' | 'todos' | 'intervalo'
  const [mes, setMes] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const escolherMes = m => { setPeriodo('mes'); setMes(m); };
  const [unidade, setUnidade] = useState('');
  const [operadora, setOperadora] = useState('');
  const [pacote, setPacote] = useState('');
  const [lista, setLista] = useState('renovar');
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setCarregando(true); setErro('');
    try { setDados(await buscarIW('iw-orcamentos')); } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  // Nome de pacote padronizado: a grafia mais usada de cada pacote
  const itens = useMemo(() => {
    const brutos = dados?.itens || [];
    const grafias = {};
    brutos.forEach(o => { const k = chaveTexto(o.pacote); (grafias[k] = grafias[k] || {})[o.pacote] = (grafias[k][o.pacote] || 0) + 1; });
    const nome = Object.fromEntries(Object.entries(grafias).map(([k, g]) => [k, Object.entries(g).sort((a, b) => b[1] - a[1])[0][0]]));
    return brutos.map(o => ({ ...o, pacote: nome[chaveTexto(o.pacote)], mes: o.inicio.slice(0, 7) }));
  }, [dados]);

  const meses = useMemo(() => [...new Set(itens.map(o => o.mes))].sort(), [itens]);
  const hoje = hojeISO();
  // Período: um mês (padrão = mês atual), "todos" ou "intervalo" (de/até pela data de início do orçamento)
  const mesPadrao = meses.includes(hoje.slice(0, 7)) ? hoje.slice(0, 7) : meses[meses.length - 1] || '';
  const mesSel = periodo === 'mes' ? (mes || mesPadrao) : '';
  const rotuloPeriodo = periodo === 'todos' ? 'todos os meses'
    : periodo === 'intervalo' ? `${de ? fmtData(de) : 'início'} a ${ate ? fmtData(ate) : 'hoje em diante'}`
      : mesSel ? fmtMes(mesSel) : '—';
  const noPeriodo = o => (periodo === 'todos' ? true : periodo === 'intervalo' ? (!de || o.inicio >= de) && (!ate || o.inicio <= ate) : o.mes === mesSel);

  // Filtros de unidade / operadora / pacote valem para todos os meses
  const base = useMemo(() => itens.filter(o => (!unidade || o.unidade === unidade) && (!operadora || o.operadora === operadora) && (!pacote || o.pacote === pacote)), [itens, unidade, operadora, pacote]);
  const doMes = useMemo(() => base.filter(noPeriodo), [base, periodo, mesSel, de, ate]); // eslint-disable-line react-hooks/exhaustive-deps
  const ativosMes = useMemo(() => doMes.filter(ativo), [doMes]);
  const operadoras = useMemo(() => [...new Set(itens.filter(o => !unidade || o.unidade === unidade).map(o => o.operadora))].sort(), [itens, unidade]);
  const pacotes = useMemo(() => [...new Set(itens.map(o => o.pacote))].sort((a, b) => a.localeCompare(b, 'pt-BR')), [itens]);

  // Renovar: último orçamento do paciente no mês vai até o fim do mês, sem alta, e não há orçamento no mês seguinte.
  const renovar = useMemo(() => {
    if (!mesSel) return null;
    const prox = mesSeguinte(mesSel);
    if (!itens.some(o => o.mes === prox)) return null; // mês seguinte ainda não foi orçado para ninguém
    const temProx = new Set(base.filter(o => o.mes === prox && ativo(o)).map(o => o.numIW));
    const ultimo = {};
    ativosMes.forEach(o => { if (!ultimo[o.numIW] || o.fim > ultimo[o.numIW].fim) ultimo[o.numIW] = o; });
    const fim = fimDoMes(mesSel);
    return Object.values(ultimo).filter(o => o.fim >= fim && (!o.alta || o.alta > fim) && !temProx.has(o.numIW));
  }, [itens, base, ativosMes, mesSel]);

  const listas = useMemo(() => ({
    renovar: renovar || [],
    pendentes: ativosMes.filter(o => o.autorizacaoInterna === '1' && o.inicio <= hoje),
    semValor: ativosMes.filter(o => o.valor === null),
    parciais: ativosMes.filter(o => o.inicio.slice(8) !== '01' || o.fim < fimDoMes(o.mes)),
    todos: doMes,
  }), [renovar, ativosMes, doMes, hoje]);

  const q = chaveTexto(busca);
  const linhas = useMemo(() => (listas[lista] || [])
    .filter(o => !q || chaveTexto(`${o.paciente} ${o.numIW} ${o.operadora} ${o.pacote}`).includes(q))
    .sort((a, b) => (b.valor ?? -1) - (a.valor ?? -1) || a.paciente.localeCompare(b.paciente, 'pt-BR')), [listas, lista, q]);

  const porMes = useMemo(() => meses.map(m => {
    const doM = base.filter(o => o.mes === m && ativo(o));
    const linha = { mes: m, rotulo: fmtMes(m), detalhe: `${new Set(doM.map(o => o.numIW)).size} pacientes · ${doM.filter(o => o.valor !== null).length} de ${doM.length} com valor` };
    UNIDADES.forEach(u => { linha[u] = soma(doM.filter(o => o.unidade === u)); });
    linha.total = soma(doM);
    linha.noPeriodo = (!de || fimDoMes(m) >= de) && (!ate || `${m}-01` <= ate);
    return linha;
  }), [base, meses, de, ate]);
  const unidadesNoGrafico = UNIDADES.filter(u => porMes.some(l => l[u] > 0));

  const agrupar = (campo, max, outros) => {
    const g = {};
    ativosMes.forEach(o => { const k = o[campo]; g[k] = g[k] || { nome: k, valor: 0, n: 0, unidade: o.unidade }; g[k].valor += o.valor || 0; g[k].n += 1; });
    const ord = Object.values(g).sort((a, b) => b.valor - a.valor);
    const top = ord.slice(0, max);
    const resto = ord.slice(max);
    if (resto.length) top.push({ nome: `${outros} (${resto.length})`, valor: soma(resto), n: resto.reduce((s, x) => s + x.n, 0), resto: true });
    return top.map(x => ({ ...x, detalhe: `${x.n} orçamento(s)`, cor: x.resto ? '#cbd5e1' : campo === 'operadora' ? COR_UNIDADE[x.unidade] : COR_NEUTRA }));
  };
  const porOperadora = useMemo(() => agrupar('operadora', 12, 'Outras'), [ativosMes]); // eslint-disable-line react-hooks/exhaustive-deps
  const porPacote = useMemo(() => agrupar('pacote', 10, 'Outros'), [ativosMes]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportar = () => {
    const ws = XLSX.utils.json_to_sheet(linhas.map(o => ({
      'Nº IW': o.numIW, Paciente: o.paciente, Unidade: o.unidade, Operadora: o.operadora, Pacote: o.pacote,
      Início: fmtData(o.inicio), Fim: fmtData(o.fim), Valor: o.valor,
      'Autorização interna': (INTERNA[o.autorizacaoInterna] || [o.autorizacaoInterna])[0],
      'Autorização operadora': (OPERADORA[o.autorizacaoOperadora] || [o.autorizacaoOperadora])[0],
      Entrada: fmtData(o.entrada), Alta: o.alta ? fmtData(o.alta) : '', Comentário: o.comentario,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamentos');
    XLSX.writeFile(wb, `orcamentos_${mesSel || (periodo === 'intervalo' ? `${de || 'inicio'}_${ate || 'fim'}` : 'todos')}_${lista}.xlsx`);
  };

  const pagina = { minHeight: '100vh', background: P.bg, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 };
  if (carregando && !dados) return <div style={pagina}><div style={{ color: P.muted }}>Lendo os orçamentos do IW…</div></div>;
  if (erro && !dados) return <div style={pagina}><div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px' }}>Não foi possível carregar: {erro}</div></div>;

  const valorMes = soma(ativosMes);
  const pacMes = new Set(ativosMes.map(o => o.numIW));
  const pacComValor = new Set(ativosMes.filter(o => o.valor).map(o => o.numIW));
  const autorizados = ativosMes.filter(o => o.autorizacaoInterna === '2').length;
  const select = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', background: P.white, maxWidth: 260 };
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.8rem', color: P.text };
  const cartao = (rotulo, valor, sub, cor = P.text, aoClicar) => (
    <div onClick={aoClicar} style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 14px', minWidth: 170, flex: '1 1 170px', cursor: aoClicar ? 'pointer' : 'default' }}>
      <div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 600 }}>{rotulo}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: cor }}>{valor}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: P.muted }}>{sub}</div>}
    </div>
  );
  const eixoValor = { tick: { fontSize: 11, fill: P.muted }, axisLine: false, tickLine: false };

  return (
    <div style={pagina}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: P.text, margin: 0 }}>💰 Gestão de Orçamentos</h2>
          <div style={{ fontSize: '0.8rem', color: P.muted, marginTop: 4 }}>
            Orçamentos mensais por paciente exportados pelo IW · arquivo de {fmtExportacao(dados.atualizadoEm)} (a IW gera a cada 30 min) · reprovados não entram nas somas.
          </div>
        </div>
        <button onClick={carregar} disabled={carregando} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.8rem' }}>
          <RefreshCw size={14} /> {carregando ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: P.text, marginRight: 4 }}>📅 Período:</span>
        {meses.map(m => {
          const sel = periodo === 'mes' && mesSel === m;
          const tot = porMes.find(l => l.mes === m)?.total || 0;
          return (
            <button key={m} onClick={() => escolherMes(m)} aria-pressed={sel}
              style={{ border: `1px solid ${sel ? P.primary : P.border}`, background: sel ? P.primary : P.white, color: sel ? '#fff' : P.text, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', textAlign: 'left', lineHeight: 1.2 }}>
              <div style={{ fontSize: '0.86rem', fontWeight: 800 }}>{fmtMes(m)}{m === hoje.slice(0, 7) ? ' · atual' : ''}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{fmtCurto(tot)}</div>
            </button>
          );
        })}
        {[['todos', 'Todos os meses'], ['intervalo', 'Personalizado']].map(([k, l]) => {
          const sel = periodo === k;
          return (
            <button key={k} onClick={() => setPeriodo(k)} aria-pressed={sel}
              style={{ border: `1px solid ${sel ? P.primary : P.border}`, background: sel ? P.primary : P.white, color: sel ? '#fff' : P.muted, borderRadius: 10, padding: '9px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
              {l}
            </button>
          );
        })}
        {periodo === 'intervalo' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.78rem', color: P.muted }}>
            início do orçamento de
            <input type="date" value={de} onChange={e => setDe(e.target.value)} aria-label="De" style={{ ...select, padding: '5px 6px' }} />
            até
            <input type="date" value={ate} onChange={e => setAte(e.target.value)} aria-label="Até" style={{ ...select, padding: '5px 6px' }} />
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {['', ...UNIDADES].map(u => {
          const sel = unidade === u;
          return (
            <button key={u || 'todas'} onClick={() => { setUnidade(u); setOperadora(''); }}
              style={{ border: `1px solid ${sel ? P.primary : P.border}`, background: sel ? '#eef2ff' : P.white, color: sel ? P.primary : P.muted, borderRadius: 999, padding: '5px 12px', fontSize: '0.8rem', fontWeight: sel ? 800 : 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {u && <span style={{ width: 9, height: 9, borderRadius: 2, background: COR_UNIDADE[u] }} />}{u || 'Todas as unidades'}
            </button>
          );
        })}
        <select value={operadora} onChange={e => setOperadora(e.target.value)} style={select} aria-label="Operadora">
          <option value="">Todas as operadoras</option>
          {operadoras.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={pacote} onChange={e => setPacote(e.target.value)} style={select} aria-label="Pacote">
          <option value="">Todos os pacotes</option>
          {pacotes.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {(unidade || operadora || pacote) && <button onClick={() => { setUnidade(''); setOperadora(''); setPacote(''); }} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.78rem' }}>limpar filtros</button>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {cartao(`Valor orçado · ${rotuloPeriodo}`, fmtCurto(valorMes), `${ativosMes.length} orçamentos`)}
        {cartao('Pacientes', pacMes.size, UNIDADES.map(u => [u, new Set(ativosMes.filter(o => o.unidade === u).map(o => o.numIW)).size]).filter(x => x[1]).map(([u, n]) => `${u} ${n}`).join(' · '))}
        {cartao(mesSel ? 'Ticket médio por paciente' : 'Média por paciente no período', pacComValor.size ? fmtCurto(valorMes / pacComValor.size) : '—', mesSel ? 'só pacientes com valor lançado' : 'soma dos meses do período ÷ pacientes')}
        {cartao('Aprovados (interna)', ativosMes.length ? `${Math.round((autorizados / ativosMes.length) * 100)}%` : '—', `${autorizados} de ${ativosMes.length} · ${ativosMes.filter(o => o.autorizacaoOperadora === '2').length} pela operadora`, '#166534')}
        {cartao('❔ Sem valor', listas.semValor.length, 'valor ainda não lançado no IW', '#92400e', () => setLista('semValor'))}
        {cartao('🔁 Renovar', renovar ? renovar.length : '—', !mesSel ? 'escolha um mês' : renovar ? `sem orçamento em ${fmtMes(mesSeguinte(mesSel))}` : `${fmtMes(mesSeguinte(mesSel))} ainda sem orçamentos`, '#991b1b', () => setLista('renovar'))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14 }}>
        <Painel titulo="Valor orçado por mês" sub="Por unidade · clique numa barra para ver o mês">
          <div style={{ display: 'flex', gap: 14, marginBottom: 6 }}>
            {unidadesNoGrafico.map(u => <span key={u} style={{ fontSize: '0.76rem', color: P.text, display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: COR_UNIDADE[u] }} />{u}</span>)}
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={porMes} margin={{ top: 18, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={P.grid} />
              <XAxis dataKey="rotulo" {...eixoValor} />
              <YAxis {...eixoValor} tickFormatter={fmtCurto} width={78} />
              <Tooltip content={<TooltipValor />} cursor={{ fill: 'rgba(99,102,241,.06)' }} />
              {unidadesNoGrafico.map((u, i) => (
                <Bar key={u} dataKey={u} name={u} stackId="m" fill={COR_UNIDADE[u]} stroke="#fff" strokeWidth={1} maxBarSize={56} radius={i === unidadesNoGrafico.length - 1 ? [4, 4, 0, 0] : 0} style={{ cursor: 'pointer' }} onClick={d => d?.payload?.mes && escolherMes(d.payload.mes)}>
                  {porMes.map(l => <Cell key={l.mes} fillOpacity={periodo === 'todos' || l.mes === mesSel || (periodo === 'intervalo' && l.noPeriodo) ? 1 : 0.45} />)}
                  {i === unidadesNoGrafico.length - 1 && <LabelList dataKey="total" position="top" formatter={fmtCurto} style={{ fontSize: 11, fill: P.text, fontWeight: 700 }} />}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Painel>

        <Painel titulo={`Autorização · ${rotuloPeriodo}`} sub="Mesmos textos do IW (Status Aprov. Interna e Status Aprov. F. Pagadora)">
          <div style={{ paddingTop: 8 }}>
            <BarraStatus titulo="Interna (Quality)" partes={[
              { l: 'Aprovado', n: doMes.filter(o => o.autorizacaoInterna === '2').length, cor: '#0ca30c' },
              { l: 'Aguardando aprovação', n: doMes.filter(o => o.autorizacaoInterna === '1').length, cor: '#fab219' },
              { l: 'Reprovado', n: doMes.filter(o => o.autorizacaoInterna === '3').length, cor: '#d03b3b' },
            ]} />
            <BarraStatus titulo="Operadora" partes={[
              { l: 'Aprovado', n: ativosMes.filter(o => o.autorizacaoOperadora === '2').length, cor: '#0ca30c' },
              { l: 'Aguardando aprovação', n: ativosMes.filter(o => o.autorizacaoOperadora === '1').length, cor: '#fab219' },
              { l: 'Reprovado', n: ativosMes.filter(o => o.autorizacaoOperadora === '3').length, cor: '#d03b3b' },
            ]} />
            <div style={{ fontSize: '0.78rem', color: P.text, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }} onClick={() => setLista('pendentes')}>
              ⏳ <strong>{listas.pendentes.length}</strong> orçamento(s) aguardando aprovação interna com o período já iniciado — ver lista
            </div>
          </div>
        </Painel>

        <Painel titulo={`Valor por operadora · ${rotuloPeriodo}`} sub="Cor = unidade · clique para filtrar">
          <ResponsiveContainer width="100%" height={Math.max(160, porOperadora.length * 28)}>
            <BarChart data={porOperadora} layout="vertical" margin={{ top: 0, right: 70, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="nome" width={170} {...eixoValor} interval={0} />
              <Tooltip content={<TooltipValor />} cursor={{ fill: 'rgba(99,102,241,.06)' }} />
              <Bar dataKey="valor" radius={[0, 4, 4, 0]} maxBarSize={18} style={{ cursor: 'pointer' }} onClick={d => { const x = d?.payload; if (x && !x.resto) setOperadora(operadora === x.nome ? '' : x.nome); }}>
                {porOperadora.map(x => <Cell key={x.nome} fill={x.cor} />)}
                <LabelList dataKey="valor" position="right" formatter={fmtCurto} style={{ fontSize: 11, fill: P.text }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Painel>

        <Painel titulo={`Valor por pacote · ${rotuloPeriodo}`} sub="Os 10 maiores · clique para filtrar">
          <ResponsiveContainer width="100%" height={Math.max(160, porPacote.length * 28)}>
            <BarChart data={porPacote} layout="vertical" margin={{ top: 0, right: 70, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="nome" width={220} {...eixoValor} interval={0} tickFormatter={s => (s.length > 34 ? `${s.slice(0, 33)}…` : s)} />
              <Tooltip content={<TooltipValor />} cursor={{ fill: 'rgba(99,102,241,.06)' }} />
              <Bar dataKey="valor" radius={[0, 4, 4, 0]} maxBarSize={18} style={{ cursor: 'pointer' }} onClick={d => { const x = d?.payload; if (x && !x.resto) setPacote(pacote === x.nome ? '' : x.nome); }}>
                {porPacote.map(x => <Cell key={x.nome} fill={x.cor} />)}
                <LabelList dataKey="valor" position="right" formatter={fmtCurto} style={{ fontSize: 11, fill: P.text }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Painel>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {LISTAS.map(l => {
          const sel = lista === l.k;
          const arr = listas[l.k];
          return (
            <button key={l.k} onClick={() => setLista(l.k)} title={l.desc}
              style={{ border: `1px solid ${sel ? P.primary : P.border}`, background: sel ? '#eef2ff' : P.white, color: sel ? P.primary : P.muted, borderRadius: 999, padding: '6px 14px', fontSize: '0.8rem', fontWeight: sel ? 800 : 600, cursor: 'pointer' }}>
              {l.l} · {l.k === 'renovar' && !renovar ? '—' : arr.length}
            </button>
          );
        })}
        <label style={{ position: 'relative', marginLeft: 'auto' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente, nº IW, operadora ou pacote</span>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Paciente, nº IW, operadora…" style={{ padding: '6px 8px 6px 28px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', width: 220 }} />
        </label>
        <button onClick={exportar} disabled={!linhas.length} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: '0.78rem' }}>
          <Download size={13} /> Excel
        </button>
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: P.muted, borderBottom: `1px solid ${P.border}` }}>
          {lista === 'renovar' && !renovar
            ? (!mesSel ? 'A lista de renovação compara um mês com o seguinte — escolha um mês no Período.'
              : `${fmtMes(mesSeguinte(mesSel))} ainda não tem orçamentos no IW — a lista de renovação aparece quando o mês seguinte começar a ser orçado.`)
            : `${LISTAS.find(l => l.k === lista).desc || 'todos os orçamentos'} · ${rotuloPeriodo}`}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
            <thead><tr>{['Paciente', 'Operadora', 'Pacote', 'Período', 'Valor', 'Interna', 'Operadora', 'Entrada · alta'].map((h, i) => <th key={i} style={{ ...th, textAlign: h === 'Valor' ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {linhas.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: P.muted, padding: 26 }}>Nada nesta lista.</td></tr>}
              {linhas.map(o => {
                const [li, fi, ci] = INTERNA[o.autorizacaoInterna] || [`código ${o.autorizacaoInterna}`, '#f1f5f9', '#475569'];
                const [lo, fo, co] = OPERADORA[o.autorizacaoOperadora] || [`código ${o.autorizacaoOperadora}`, '#f1f5f9', '#475569'];
                return (
                  <tr key={o.id} style={{ opacity: o.autorizacaoInterna === '3' ? 0.55 : 1 }}>
                    <td style={{ ...td, fontWeight: 700, maxWidth: 260 }}>{o.paciente}<div style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>IW {o.numIW} · orç. {o.id}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: COR_UNIDADE[o.unidade] }} />{o.operadora}</span>
                      <div style={{ fontSize: '0.7rem', color: P.muted }}>{o.unidade}</div>
                    </td>
                    <td style={{ ...td, maxWidth: 260 }}>{o.pacote}{o.comentario && <div style={{ fontSize: '0.7rem', color: '#92400e' }}>💬 {o.comentario}</div>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(o.inicio).slice(0, 5)} a {fmtData(o.fim).slice(0, 5)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: o.valor === null ? '#92400e' : P.text }}>{o.valor === null ? 'sem valor' : fmtValor(o.valor)}</td>
                    <td style={td}><span style={chip(fi, ci)}>{li}</span></td>
                    <td style={td}><span style={chip(fo, co)}>{lo}</span></td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '0.74rem' }}>{fmtData(o.entrada)}{o.alta && <div style={{ color: '#991b1b', fontWeight: 700 }}>alta {fmtData(o.alta)}</div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${P.border}`, fontSize: '0.78rem', color: P.muted }}>
          {linhas.length} orçamento(s) · {fmtValor(soma(linhas.filter(ativo)))}
        </div>
      </div>
    </div>
  );
}
